import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter, useFocusEffect, useNavigation } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { useShallow } from 'zustand/react/shallow';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useChatStore, useCoworkerStore } from '@/store';
import { chatsApi } from '@/lib/api';
import Button from '@/components/Buttons/Button';
import Text from '@/components/Typography/Text';
import MetaHead from '@/components/MetaHead';
import ChatView from '@/components/Chat/ChatView';
import ChatLoadingState from '@/components/Placeholders/ChatLoadingState';
import WorkLaunchEntrance from '@/components/Coworker/WorkLaunchEntrance';
import useRequireAuth, {
  useAuthHydrated,
  useAuthPresentation,
} from '@/hooks/useRequireAuth';
import { shouldPreserveAttentionTitle } from '@/lib/workTabAttention';
import { displayChatTitle } from '@/lib/chatTitle';
import {
  cleanupTemporaryChatById,
  getActiveChatRouteId,
  setActiveChatRouteId,
} from '@/lib/temporaryChatCleanup';
import { isConversationMissingError, restoreLastUserPromptToComposer } from '@/lib/restoreComposerPrompt';
import { isTransientConversationLoadError } from '@/lib/conversationLoadRecovery';

function normalizeParam(value) {
  return Array.isArray(value) ? value[0] : value;
}

function isPlaceholderChatTitle(title) {
  if (!title || typeof title !== 'string') return true;
  const normalized = title.trim().toLowerCase();
  return normalized === 'untitled chat'
    || normalized === 'new chat'
    || normalized === 'untitled conversation'
    || normalized === 'voice conversation'
    || normalized === 'voice chat'
    || normalized === 'untitled work'
    || normalized === 'coworker thread';
}

function maybeRetrieveWorkChatTitle(conversation) {
  if (!conversation?.id) return;
  const isWork = !!(conversation.flags?.isCoworkerSession || conversation.coworkerSession);
  if (!isWork || !isPlaceholderChatTitle(conversation.title)) return;
  const userId = useAuthStore.getState().user?.id;
  if (userId) useChatStore.getState().retryTitleRetrieval(userId, conversation.id);
}

function TitleBanner({ colors, title }) {
  const styles = StyleSheet.create({
    titleBanner: {
      width: '100%',
      paddingVertical: 10,
      paddingHorizontal: 15,
      alignItems: 'center',
      justifyContent: 'center',
      borderBottomWidth: 1,
      borderBottomColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
    },
  });

  return (
    <View style={styles.titleBanner}>
      <Text size='n' weight='bold' color='primary'>
        {title}
      </Text>
    </View>
  );
}

function SignUpBanner({ colors }) {
  const requireAuth = useRequireAuth();
  const styles = StyleSheet.create({
    signUpContainer: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      padding: 20,
      backgroundColor: colors.solid_color_block,
      borderTopWidth: 1,
      borderTopColor: colors.border_color_secondary,
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'center',
      gap: 12,
    },
  });

  const handleSignUp = () => {
    requireAuth(() => {}, {
      source: 'shared_chat_join',
      title: 'Sign in to join this chat',
      subtitle: 'Create a free account so we can save your reply and let the owner keep the conversation going with you.',
      icon: 'comments',
    });
  };

  return (
    <View style={styles.signUpContainer}>
      <Text size='n' color='primary'>Want to join the conversation?</Text>
      <Button
        text="Sign up"
        size='small'
        variant="primary"
        onPress={handleSignUp}
      />
    </View>
  );
}

const ChatPage = React.memo(({ chat, isLoading, isHistoryRefreshing, error, onRetryError, launchStartedAt }) => {
  const params = useLocalSearchParams();
  const id = normalizeParam(params?.id);
  const { colors } = useTheme();
  const isAuthenticated = useAuthPresentation();
  const isOwner = chat?.isOwner;
  const chatTitle = displayChatTitle(chat?.title, '');
  const isWork = !!(chat?.flags?.isCoworkerSession || chat?.coworkerSession);
  const documentTitle = chatTitle
    ? `${chatTitle}${isWork ? ' - Work' : ' - Chat'}`
    : (isWork ? 'Work' : 'AI Chat');
  // MetaHead appends "| Gab AI". Writing the short title here used to fight
  // Head + the attention MutationObserver and retrigger React updates.
  const tabTitle = documentTitle.includes('Gab AI')
    ? documentTitle
    : `${documentTitle} | Gab AI`;

  // Same-route /c/:id swaps do not always remount expo-router Head, so a new
  // Work thread can keep the previous tab title (Measurebox on a Trailmark URL).
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    if (shouldPreserveAttentionTitle(`/c/${id}`)) return;
    if (document.title !== tabTitle) document.title = tabTitle;
  }, [id, tabTitle]);

  const containerStyle = useMemo(() => ({
      flex: 1,
      backgroundColor: colors.solid_color_primary,
  }), [colors.solid_color_primary]);

  return (
    <WorkLaunchEntrance key={id} startedAt={launchStartedAt} style={containerStyle}>
      <MetaHead
        key={id}
        title={tabTitle}
        description={isAuthenticated 
          ? "Continue your AI conversation. Get real-time responses, share insights, and explore topics with advanced AI models."
          : "View this AI chat conversation on Gab AI - where intelligent discussions come to life through advanced AI models."
        }
        noindex
      />
          {(!isAuthenticated || !isOwner) && chatTitle && (
            <TitleBanner colors={colors} title={chatTitle} />
          )}
      {isLoading ? <ChatLoadingState /> : (
        <ChatView
          conversationId={id}
          chat={chat}
          isHistoryRefreshing={isHistoryRefreshing}
          error={error}
          onRetryError={onRetryError}
        />
      )}
      {!isAuthenticated && (
        <SignUpBanner colors={colors} />
      )}
    </WorkLaunchEntrance>
  );
});

function ChatRoute() {
  const params = useLocalSearchParams();
  const id = normalizeParam(params?.id);
  const router = useRouter();
  const navigation = useNavigation();
  const isAuthHydrated = useAuthHydrated();
  const isAuthenticated = useAuthPresentation();
  const userId = useAuthStore(state => state.user?.id);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingConversationId, setLoadingConversationId] = useState(id);
  const [refreshingConversationId, setRefreshingConversationId] = useState(null);
  const [error, setError] = useState(null);
  const [fetchTick, setFetchTick] = useState(0);
  const [publicChat, setPublicChat] = useState(null);
  // Metadata only — streaming replaces the chat/messages objects every 50ms.
  // ChatView reads the live chat from the store so this route does not
  // rebuild MetaHead / banners / ChatPage on every token.
  const regularChat = useChatStore(useShallow((state) => {
    const found = state.chats.find((c) => c.id === id);
    if (!found) return null;
    return {
      id: found.id,
      title: found.title,
      isOwner: found.isOwner,
      flags: found.flags,
      coworkerSession: found.coworkerSession,
      user: found.user,
      visibility: found.visibility,
      hasMessages: !!found.messages?.length,
    };
  }));
  const addChats = useChatStore(state => state.addChats);
  const deletingChatId = useChatStore(state => state.deletingChatId);
  const deleteChat = useChatStore(state => state.deleteChat);
  const previousIdRef = useRef(id);

  useEffect(() => {
    setActiveChatRouteId(id);
    return () => {
      if (getActiveChatRouteId() === String(id)) {
        setActiveChatRouteId(null);
      }
    };
  }, [id]);

  useEffect(() => {
    if (__DEV__) {
      console.log('[ChatRoute] Store data:', {
        id,
        regularChatExists: !!regularChat,
        regularChatMessageCount: regularChat?.messages?.length,
        totalChatsInStore: useChatStore.getState().chats.length,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, !!regularChat]);

  const chat = isAuthenticated ? regularChat : publicChat;

  // A just-created Work chat already has an accepted task and the submitted
  // prompt. Keep that visible while detail loads instead of flashing a second
  // skeleton. Capture this per route: the pending bubble can settle before
  // the detail response. Older sidebar summaries still need their normal load.
  const hasWorkLaunchPreview = useMemo(() => {
    if (Platform.OS !== 'web' || !isAuthenticated || !userId) return false;
    const seededChat = useChatStore.getState().chats.find(c => c.id === id);
    if (!seededChat?.isOwner) return false;
    const rawTaskId = seededChat.coworkerSession;
    const taskId = typeof rawTaskId === 'object' ? rawTaskId?.id || rawTaskId?._id : rawTaskId;
    const work = useCoworkerStore.getState();
    if (work.tasks?.[taskId]?.conversationId !== id) return null;
    return work.pendingSendsByTask?.[taskId]?.find(send => send.id === `launch-${taskId}`) || null;
  }, [id, userId, isAuthenticated]);

  // Cleanup temporary chats on navigate away.
  //
  // Native Drawer reuses a single `c/[id]` screen instance. Leaving a temp
  // chat can happen via:
  //   - blur / unmount → useFocusEffect + useEffect cleanup
  //   - same-route param change (history tap) → previous-id effect below
  //     (blur does NOT fire when only the `id` param changes)
  //   - beforeRemove → covers stack pops / drawer replaces that skip blur
  //
  // cleanupTemporaryChatById is idempotent: it re-reads the store, no-ops if
  // the chat is gone or not temporary, and only fires the API delete once.
  const cleanupTempChat = useCallback(() => {
    cleanupTemporaryChatById(id, deleteChat);
  }, [id, deleteChat]);

  // Focus (not just mount) owns the "live route" marker: the native drawer
  // keeps this screen mounted on blur, so the mount effect above never
  // clears it and a temp chat would otherwise survive leaving via the drawer.
  useFocusEffect(
    useCallback(() => {
      setActiveChatRouteId(id);
      return () => {
        if (getActiveChatRouteId() === String(id)) {
          setActiveChatRouteId(null);
        }
        cleanupTempChat();
      };
    }, [id, cleanupTempChat])
  );

  useEffect(() => {
    return cleanupTempChat;
  }, [cleanupTempChat]);

  // Drawer history navigation: navigate('c/[id]', { id }) keeps focus, so
  // useFocusEffect never runs. Explicitly clean up the previous temp chat
  // whenever the route param changes.
  useEffect(() => {
    const previousId = previousIdRef.current;
    if (previousId && previousId !== id) {
      cleanupTemporaryChatById(previousId, deleteChat);
    }
    previousIdRef.current = id;
  }, [id, deleteChat]);

  useEffect(() => {
    const unsubscribe = navigation.addListener?.('beforeRemove', () => {
      cleanupTemporaryChatById(previousIdRef.current || id, deleteChat);
    });
    return typeof unsubscribe === 'function' ? unsubscribe : undefined;
  }, [navigation, id, deleteChat]);

  // Fetch chat data if not available in store
  useEffect(() => {
    let cancelled = false;
    setLoadingConversationId(id);
    setRefreshingConversationId(null);
    if (!isAuthHydrated) {
      // Keep the loading state until we know whether the user is signed in.
      setIsLoading(true);
      return;
    }

    // Stale-while-revalidate: a cached chat renders instantly, then a
    // background detail fetch refreshes it so messages sent from web or
    // another device show up without a manual reload. The addChats merge
    // preserves in-flight/streaming state, so this is safe even mid-turn.
    const revalidateConversationInBackground = async (conversationId) => {
      setRefreshingConversationId(conversationId);
      try {
        const data = await chatsApi.getConversationById(conversationId);
        if (cancelled) return;
        if (!data) return;
        const currentUserId = useAuthStore.getState().user?.id || userId;
        const dataUserId = String(data.user?._id || data.user?.id || data.user);
        if (currentUserId && dataUserId !== String(currentUserId) && data.visibility !== 'public') {
          return;
        }
        const localChat = useChatStore.getState().chats.find(c => c.id === conversationId);
        if (localChat?.isInferencing || localChat?.isInferencingWaiting) {
          return;
        }
        addChats([data]);
        maybeRetrieveWorkChatTitle(data);
        // Another device may be mid-generation — mirror the cold-fetch path
        // so the waiting UI shows here too. Applied after addChats because
        // the merge defers to REST payloads for chat-level flags.
        if (data.flags?.isInferencing) {
          const refreshedChat = useChatStore.getState().chats.find(c => c.id === conversationId);
          if (!refreshedChat?.isInferencing) {
            useChatStore.getState().beginInferencingConversation(conversationId);
          }
        }
      } catch (_) { /* background revalidation is best-effort */ }
      finally {
        if (!cancelled) setRefreshingConversationId(current => current === conversationId ? null : current);
      }
    };

    const fetchChatData = async () => {
      try {
        // Clear any stale error/public-chat state left over from a previous
        // conversation id. On native the drawer reuses this single `c/[id]`
        // screen instance across chats (unmountOnBlur is unreliable in nested
        // navigators — see the cleanup note above), so a "Conversation not
        // found" error set while fetching a just-deleted chat would otherwise
        // persist when the same instance is reused for a brand-new chat that
        // short-circuits below before reaching the in-try setError(null).
        setError(null);

        if (deletingChatId === id) {
          setIsLoading(false);
          return;
        }

        if (isAuthenticated) {
          const freshChats = useChatStore.getState().chats;
          const existingChat = freshChats.find(c => c.id === id);
          // Sidebar summaries are not loaded conversations. Always fetch
          // their detail, even if the Work event listener already attached.
          if (existingChat?.messages?.length > 0) {
            setIsLoading(false);
            maybeRetrieveWorkChatTitle(existingChat);
            const taskId = existingChat.coworkerSession;
            const following = taskId && useCoworkerStore.getState().followingByTask?.[taskId];
            if (!following && !existingChat.isInferencing && !existingChat.isInferencingWaiting) {
              revalidateConversationInBackground(id);
            }
            return;
          }
        }

        setIsLoading(true);
        setError(null);

        const data = await chatsApi.getConversationById(id);
        if (cancelled) return;

        if (!data) {
          throw new Error('Chat not found');
        }

        const freshChatsAfterFetch = useChatStore.getState().chats;
        const chatAddedWhileFetching = freshChatsAfterFetch.find(c => c.id === id);
        if (chatAddedWhileFetching && chatAddedWhileFetching.messages?.length > 0) {
          maybeRetrieveWorkChatTitle(chatAddedWhileFetching);
          setIsLoading(false);
          return;
        }

        if (isAuthenticated) {
          const currentUserId = useAuthStore.getState().user?.id || userId;
          const dataUserId = String(data.user?._id || data.user?.id || data.user);
          if (!currentUserId || dataUserId === String(currentUserId) || data.visibility === 'public') {
            addChats([data]);
            maybeRetrieveWorkChatTitle(data);
            if (data.flags?.isInferencing) {
              useChatStore.getState().beginInferencingConversation(id);
            }
          } else {
            if (data.visibility === 'public') {
              setPublicChat(data);
            } else {
              throw new Error('You do not have access to this chat');
            }
          }
        } else {
          if (data.visibility === 'public') {
            setPublicChat(data);
          } else {
            throw new Error('This chat is private. Please log in to view it.');
          }
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching chat:', error);
        setError(error);

        const looksMissing = error.status === 404
          || isConversationMissingError(error.message)
          || /chat not found/i.test(error.message || '');
        if (looksMissing) {
          restoreLastUserPromptToComposer(id, { contextId: 'new' });
        }

        if (error.status === 404) {
          if (isAuthenticated || deletingChatId === id) {
            router.replace('/app');
          } else {
            router.replace('/404');
          }
        } else if (error.status === 403) {
          // Never redirect on 403 — stay on /c/:id and say WHY. Bouncing to
          // /app (or /) reads as "the link just goes to the homepage" when
          // someone shares a chat they forgot to make public, and the old
          // unauthenticated redirect also wiped the conversation URL on
          // reload before auth had resolved.
          setError("This conversation is private. The owner hasn't made it public.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchChatData();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isAuthenticated, isAuthHydrated, router, addChats, userId, deletingChatId, fetchTick]);

  const handleRetryError = useCallback(() => {
    setError(null);
    setIsLoading(true);
    setFetchTick((tick) => tick + 1);
  }, []);

  // Work events can fill the transcript before OR after a slow detail request
  // fails. A transient read failure must not cover those recovered messages.
  // Keep genuine missing/private/auth errors visible, even with cached rows.
  const hasRecoveredMessages = isAuthenticated && chat?.isOwner && chat?.hasMessages
    && isTransientConversationLoadError(error);

  return (
    <ChatPage
      chat={chat}
      launchStartedAt={hasWorkLaunchPreview?.launchStartedAt}
      isHistoryRefreshing={refreshingConversationId === id}
      isLoading={!isAuthHydrated || (!chat?.hasMessages && !hasWorkLaunchPreview && (loadingConversationId !== id || isLoading))}
      error={loadingConversationId === id && !hasRecoveredMessages ? error : null}
      onRetryError={handleRetryError}
    />
  );
}

function FocusedChatRoute() {
  const isFocused = useIsFocused();
  // Native-stack on web retains pushed screens with display:none; its
  // freezeOnBlur/unmountOnBlur options do not unmount their React trees.
  // Release the old chat's streams, timers and subscriptions on navigation.
  // Messages remain cached in the store for an immediate return/back open.
  if (Platform.OS === 'web' && !isFocused) return null;
  return <ChatRoute />;
}

export default React.memo(FocusedChatRoute);
